import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, Input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { MultiShipmentExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { createLabelVariablesConfig, LineItemAssignmentStatusForm, MULTI_SHIPMENT_DEFAULTS } from '../../model';

@Component({
  selector: 'multi-shipment-line-item-assignment-status',
  templateUrl: './line-item-assignment-status.component.html',
  styleUrls: ['./line-item-assignment-status.component.scss'],
  standalone: false,
})
export class LineItemAssignmentStatusComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<LineItemAssignmentStatusForm>;

  readonly cornerRadiusOptions: DropdownOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
  ];
  readonly assignedCornerRadiusOption = signal<DropdownOption>(this.cornerRadiusOptions[1]);
  readonly unassignedCornerRadiusOption = signal<DropdownOption>(this.cornerRadiusOptions[1]);

  /** Opens the dropdown above its trigger (the unassigned pill sits low in the panel). */
  readonly dropUpOverlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
  ];

  readonly assignedLabelVariables = createLabelVariablesConfig(['quantity', 'name', 'number']);
  readonly unassignedLabelVariables = createLabelVariablesConfig(['quantity']);

  private readonly defaults = MULTI_SHIPMENT_DEFAULTS.lineItemAssignmentStatus;

  get assignedBackgroundColorControl(): FormControl<string> {
    return this.formGroup.controls.assigned.controls.backgroundColor;
  }

  get assignedTextColorControl(): FormControl<string> {
    return this.formGroup.controls.assigned.controls.textColor;
  }

  get unassignedBackgroundColorControl(): FormControl<string> {
    return this.formGroup.controls.unassigned.controls.backgroundColor;
  }

  get unassignedTextColorControl(): FormControl<string> {
    return this.formGroup.controls.unassigned.controls.textColor;
  }

  ngOnInit(): void {
    this.assignedCornerRadiusOption.set(this.toOption(this.formGroup.controls.assigned.controls.cornerRadius.value));
    this.unassignedCornerRadiusOption.set(
      this.toOption(this.formGroup.controls.unassigned.controls.cornerRadius.value),
    );
  }

  onAssignedCornerRadiusChange(option: DropdownOption): void {
    this.assignedCornerRadiusOption.set(option);
    this.formGroup.controls.assigned.controls.cornerRadius.setValue(
      option.value as MultiShipmentExtension.PillCornerRadius,
    );
    this.formGroup.controls.assigned.controls.cornerRadius.markAsDirty();
  }

  onUnassignedCornerRadiusChange(option: DropdownOption): void {
    this.unassignedCornerRadiusOption.set(option);
    this.formGroup.controls.unassigned.controls.cornerRadius.setValue(
      option.value as MultiShipmentExtension.PillCornerRadius,
    );
    this.formGroup.controls.unassigned.controls.cornerRadius.markAsDirty();
  }

  onAssignedBackgroundColorCleared(): void {
    this.resetColor(this.assignedBackgroundColorControl, this.defaults.assigned.backgroundColor);
  }

  onAssignedTextColorCleared(): void {
    this.resetColor(this.assignedTextColorControl, this.defaults.assigned.textColor);
  }

  onUnassignedBackgroundColorCleared(): void {
    this.resetColor(this.unassignedBackgroundColorControl, this.defaults.unassigned.backgroundColor);
  }

  onUnassignedTextColorCleared(): void {
    this.resetColor(this.unassignedTextColorControl, this.defaults.unassigned.textColor);
  }

  private resetColor(control: FormControl<string>, defaultColor: string): void {
    control.setValue(defaultColor);
    control.markAsDirty();
  }

  private toOption(value: MultiShipmentExtension.PillCornerRadius): DropdownOption {
    return this.cornerRadiusOptions.find((option) => option.value === value) ?? this.cornerRadiusOptions[1];
  }
}
